import { PlanType } from '../../../store/funnelV3Store';
import { getExitIntentPricesForPaywall, getPlanBasePricesCents } from '../../../lib/pricingTier';

/** Matches paywall returning-user offer (REACT_APP_FINAL_COUPON_ID on server). */
export const BACK_FROM_PAYMENT_DISCOUNT_PERCENT = 10;

interface PlanPriceInput {
    firstChargePrice?: number;
    price: number;
}

interface EffectiveChargeInput {
    baseAmount: number;
    selectedPlan: PlanType | null;
    exitDiscountActive: boolean;
    promoDiscountPercent: number;
    /** Applied when no Stripe promo percent applies (matches REFERRAL10 server-side). */
    referralDiscountPercent?: number;
    /** Extra returning-user discount when server applies FINAL_COUPON. */
    backFromPayment?: boolean;
}

interface PromoDiscountAmountInput {
    baseAmount: number;
    exitDiscountActive: boolean;
    promoDiscountPercent: number;
    referralDiscountPercent?: number;
}

export const getBaseChargeAmount = (plan: PlanPriceInput): number => {
    return (plan.firstChargePrice || plan.price) / 100;
};

export const getEffectiveChargeAmount = ({
    baseAmount,
    selectedPlan,
    exitDiscountActive,
    promoDiscountPercent,
    referralDiscountPercent = 0,
    backFromPayment = false,
}: EffectiveChargeInput): number => {
    if (exitDiscountActive && selectedPlan) {
        const exitPrices = getExitIntentPricesForPaywall();
        const exitPrice = exitPrices[selectedPlan];
        if (exitPrice !== undefined) return exitPrice;
    }

    const pct =
        promoDiscountPercent > 0 ? promoDiscountPercent : referralDiscountPercent;
    if (pct > 0) {
        return baseAmount * (1 - pct / 100);
    }

    if (backFromPayment) {
        return baseAmount * (1 - BACK_FROM_PAYMENT_DISCOUNT_PERCENT / 100);
    }

    return baseAmount;
};

export interface ExpressCheckoutAmountInput {
    selectedPlan: PlanType | null;
    checkoutBaseAmountCents: number;
    exitDiscountActive: boolean;
    promoDiscountPercent: number;
    referralDiscountPercent?: number;
    backFromPayment?: boolean;
}

/** Charge amount (USD cents) for Stripe Express Checkout — mirrors payment UI total. */
export function getExpressCheckoutAmountCents(input: ExpressCheckoutAmountInput): number | undefined {
    const { selectedPlan, checkoutBaseAmountCents } = input;
    if (!selectedPlan) return undefined;

    const baseAmountCents =
        checkoutBaseAmountCents > 0
            ? checkoutBaseAmountCents
            : getPlanBasePricesCents()[selectedPlan];

    if (baseAmountCents == null || baseAmountCents <= 0) return undefined;

    const effectiveDollars = getEffectiveChargeAmount({
        baseAmount: baseAmountCents / 100,
        selectedPlan,
        exitDiscountActive: input.exitDiscountActive,
        promoDiscountPercent: input.promoDiscountPercent,
        referralDiscountPercent: input.referralDiscountPercent ?? 0,
        backFromPayment: input.backFromPayment ?? false,
    });

    const effectiveCents = Math.round(effectiveDollars * 100);
    // Stripe payment-mode Elements reject amount 0. A 100% promo is a $0 first
    // charge — skip Express Checkout and collect a card for the renewal only.
    if (effectiveCents <= 0) return undefined;

    return Math.max(50, effectiveCents);
}

/** Intro markdown vs list price, excluding any extra Stripe promo. */
export function getIntroDiscountAmount(original: number, baseAmount: number): string {
    return Math.max(0, original - baseAmount).toFixed(2);
}

/** Full first-charge savings vs list price, including a 100% promo ($0 due). */
export function getCheckoutSavings(original: number, effectiveAmount: number): string {
    return Math.max(0, original - effectiveAmount).toFixed(2);
}

export const getDisplayPrice = (effectiveAmount: number): string => {
    return effectiveAmount.toFixed(2);
};

export const getPromoDiscountAmount = ({
    baseAmount,
    exitDiscountActive,
    promoDiscountPercent,
    referralDiscountPercent = 0,
}: PromoDiscountAmountInput): string => {
    if (exitDiscountActive) return '0.00';
    const pct =
        promoDiscountPercent > 0 ? promoDiscountPercent : referralDiscountPercent;
    if (pct <= 0) return '0.00';
    return ((baseAmount * pct) / 100).toFixed(2);
};
